/**
 * Create User Service
 *
 * Calls the create-user Edge Function to create an Auth user with password
 * and a matching public.users row. Optionally assigns tenant/workspace and role.
 */

import { getSupabaseClient } from './supabaseClient';

export interface CreateUserInput {
  email: string;
  password: string;
  name?: string;
  tenantId?: string;
  workspaceId?: string;
  role?: 'member' | 'tenant-admin' | 'workspace-admin';
  createdBy?: string;
}

export interface CreateUserResult {
  userId: string;
  email: string;
  /** True when an existing login was linked to the workspace (no new Auth user). */
  linked?: boolean;
}

/** Supabase Auth rejects duplicate emails with messages like this; offer "add to workspace" instead. */
export function isAuthEmailAlreadyRegisteredMessage(message: string): boolean {
  const m = message.toLowerCase();
  return (
    m.includes('already been registered') ||
    m.includes('already registered') ||
    m.includes('user already registered') ||
    m.includes('email address is already registered') ||
    m.includes('a user with this email')
  );
}

export interface LinkExistingUserInput {
  email: string;
  name?: string;
  tenantId?: string;
  workspaceId?: string;
  role?: 'member' | 'tenant-admin' | 'workspace-admin';
  createdBy?: string;
}

export async function linkExistingUserToWorkspace(input: LinkExistingUserInput): Promise<CreateUserResult> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.functions.invoke('create-user', {
    body: {
      email: input.email.trim().toLowerCase(),
      name: input.name?.trim() || undefined,
      tenantId: input.tenantId || undefined,
      workspaceId: input.workspaceId || undefined,
      role: input.role ?? 'member',
      createdBy: input.createdBy || undefined,
      linkExistingUser: true,
    },
  });

  if (error) {
    const ctx = (error as Record<string, unknown>).context;
    if (ctx && typeof (ctx as Response).json === 'function') {
      try {
        const body = await (ctx as Response).json() as { error?: string };
        if (body?.error) throw new Error(body.error);
      } catch (parseErr) {
        if (parseErr instanceof Error && parseErr.message !== 'Failed to parse') {
          throw parseErr;
        }
      }
    }
    const msg = error.message || 'Failed to add user';
    const isNotFound = msg.includes('404') || msg.includes('Failed to fetch') || msg.includes('Load failed');
    const hint = isNotFound ? ' Deploy the function: supabase functions deploy create-user' : '';
    throw new Error(msg + hint);
  }
  if (data?.error) {
    throw new Error(typeof data.error === 'string' ? data.error : JSON.stringify(data.error));
  }
  if (!data?.userId || !data?.email) {
    throw new Error('Invalid response from create-user');
  }
  return { userId: data.userId, email: data.email, linked: true };
}

export async function createUserWithPassword(input: CreateUserInput): Promise<CreateUserResult> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.functions.invoke('create-user', {
    body: {
      email: input.email.trim().toLowerCase(),
      password: input.password,
      name: input.name?.trim() || undefined,
      tenantId: input.tenantId || undefined,
      workspaceId: input.workspaceId || undefined,
      // Always send role so the Edge Function never defaults to admin: 'member' | 'workspace-admin' | 'tenant-admin'
      role: input.role ?? 'member',
      createdBy: input.createdBy || undefined,
    },
  });

  if (error) {
    // Try to extract the actual error body from the function response.
    // FunctionsHttpError has a .context property containing the Response object.
    const ctx = (error as Record<string, unknown>).context;
    if (ctx && typeof (ctx as Response).json === 'function') {
      try {
        const body = await (ctx as Response).json() as { error?: string };
        if (body?.error) throw new Error(body.error);
      } catch (parseErr) {
        if (parseErr instanceof Error && parseErr.message !== 'Failed to parse') {
          throw parseErr;
        }
      }
    }
    // Fall back to the raw message, but only suggest deploying on a true 404.
    const msg = error.message || 'Failed to create user';
    const isNotFound = msg.includes('404') || msg.includes('Failed to fetch') || msg.includes('Load failed');
    const hint = isNotFound ? ' Deploy the function: supabase functions deploy create-user' : '';
    throw new Error(msg + hint);
  }
  if (data?.error) {
    throw new Error(typeof data.error === 'string' ? data.error : JSON.stringify(data.error));
  }
  if (!data?.userId || !data?.email) {
    throw new Error('Invalid response from create-user');
  }
  return { userId: data.userId, email: data.email, linked: false };
}
